import { ValidationResult } from '../../types/schemaMapperTypes';
import { z } from 'zod';

/**
 * Validates a field value against the Zod schema
 */
export const validateFieldValue = (
  zodSchema: z.ZodTypeAny | undefined,
  fieldName: string,
  value: unknown,
): ValidationResult => {
  if (!zodSchema || !fieldName) {
    return { isValid: true }; // If no schema or field name, consider it valid
  }

  try {
    // Split the path into segments
    const segments = fieldName.split('.');
    let currentSchema = zodSchema;

    // For nested fields, we need to traverse the schema
    if (segments.length > 1) {
      for (let i = 0; i < segments.length - 1; i++) {
        const segment = segments[i];

        // Unwrap optional schemas if needed
        if (currentSchema._def.typeName === 'ZodOptional') {
          currentSchema = currentSchema._def.innerType;
        }

        if (!(currentSchema instanceof z.ZodObject)) {
          // Try to handle arrays or other container types
          if (
            'innerType' in currentSchema._def &&
            currentSchema._def.innerType
          ) {
            currentSchema = currentSchema._def.innerType;
            i--; // Retry with inner type
            continue;
          }
          return {
            isValid: false,
            errorMessage: `Invalid schema path at ${segment}`,
          };
        }

        currentSchema = currentSchema.shape[segment];
        if (!currentSchema) {
          return {
            isValid: false,
            errorMessage: `Field ${segment} not found in schema`,
          };
        }
      }

      // Get the final field schema
      const finalSegment = segments[segments.length - 1];

      // Unwrap optional schemas for the final parent schema
      if (currentSchema._def.typeName === 'ZodOptional') {
        currentSchema = currentSchema._def.innerType;
      }

      if (
        currentSchema instanceof z.ZodObject &&
        finalSegment in currentSchema.shape
      ) {
        currentSchema = currentSchema.shape[finalSegment];
      } else {
        return {
          isValid: false,
          errorMessage: `Field ${finalSegment} not found in schema`,
        };
      }
    } else {
      // For top-level fields
      if (
        currentSchema instanceof z.ZodObject &&
        fieldName in currentSchema.shape
      ) {
        currentSchema = currentSchema.shape[fieldName];
      } else {
        return {
          isValid: false,
          errorMessage: `Field ${fieldName} not found in schema`,
        };
      }
    }

    // Now validate the value against the field schema
    const result = currentSchema.safeParse(value);
    if (result.success) {
      return { isValid: true };
    } else {
      return {
        isValid: false,
        errorMessage: result.error.errors.map((err) => err.message).join(', '),
      };
    }
  } catch (error) {
    console.error('Validation error:', error);
    return { isValid: false, errorMessage: (error as Error).message };
  }
};
